import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import RichTextEditor from '@/components/RichTextEditor';
import RichText from '@/components/RichText';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BarChart3, Image, Eye, MousePointer, Upload, Trash2, Calendar,
  ExternalLink, Building2, Edit, Save, X as XIcon, Clock, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = '';

export default function SponsorPortalPage({ user }) {
  const navigate = useNavigate();
  const [sponsor, setSponsor] = useState(null);
  const [banners, setBanners] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', description: '', website: '' });
  const [bannerForm, setBannerForm] = useState({ banner_type: 'top', placement: 'app', click_url: '', campaign_start: '', campaign_end: '', file: null });
  const [uploading, setUploading] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [sponsorRes, bannersRes, analyticsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/sponsor/me`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/sponsor/me/banners`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/sponsor/me/analytics`, { credentials: 'include' }),
      ]);
      if (sponsorRes.ok) {
        const s = await sponsorRes.json();
        setSponsor(s);
        setProfileForm({ name: s.name || '', description: s.description || '', website: s.website || '' });
      } else {
        navigate('/');
        toast.error('No sponsor account linked to your profile');
        return;
      }
      if (bannersRes.ok) setBanners(await bannersRes.json());
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
    } catch (e) {
      toast.error('Failed to load sponsor data');
    }
    setLoading(false);
  };

  const handleSaveProfile = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/sponsor/me`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileForm),
      });
      if (!res.ok) throw new Error('Failed to update');
      const updated = await res.json();
      setSponsor(updated);
      setEditingProfile(false);
      toast.success('Profile updated');
    } catch (e) { toast.error(e.message); }
  };

  const handleUploadBanner = async () => {
    if (!bannerForm.file) { toast.error('Please select a banner image'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', bannerForm.file);
      formData.append('banner_type', bannerForm.banner_type);
      formData.append('placement', bannerForm.placement);
      formData.append('click_url', bannerForm.click_url);
      if (bannerForm.campaign_start) formData.append('campaign_start', bannerForm.campaign_start);
      if (bannerForm.campaign_end) formData.append('campaign_end', bannerForm.campaign_end);

      const res = await fetch(`${BACKEND_URL}/api/sponsor/me/banners`, {
        method: 'POST', credentials: 'include', body: formData,
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Upload failed'); }
      toast.success('Banner uploaded! It will be reviewed by an admin before going live.');
      setShowUpload(false);
      setBannerForm({ banner_type: 'top', placement: 'app', click_url: '', campaign_start: '', campaign_end: '', file: null });
      loadAll();
    } catch (e) { toast.error(e.message); }
    setUploading(false);
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!(await window.appConfirm('Delete this banner?'))) return;
    try {
      await fetch(`${BACKEND_URL}/api/sponsor/me/banners/${bannerId}`, {
        method: 'DELETE', credentials: 'include',
      });
      toast.success('Banner deleted');
      loadAll();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <main className="pt-24 pb-12 px-6 flex justify-center">
          <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full" />
        </main>
      </div>
    );
  }

  if (!sponsor) return null;

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-12 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-1">
              <Building2 className="h-6 w-6 text-brand-navy" />
              <h1 className="text-3xl font-bold font-heading gradient-text" data-testid="sponsor-portal-title">Sponsor Portal</h1>
            </div>
            <p className="text-slate-500">{sponsor.name}</p>
          </div>

          <Tabs defaultValue="dashboard" className="space-y-6">
            <TabsList className="flex-wrap h-auto gap-1">
              <TabsTrigger data-testid="sp-dashboard-tab" value="dashboard">
                <BarChart3 className="h-4 w-4 mr-2" /> Dashboard
              </TabsTrigger>
              <TabsTrigger data-testid="sp-banners-tab" value="banners">
                <Image className="h-4 w-4 mr-2" /> My Banners ({banners.length})
              </TabsTrigger>
              <TabsTrigger data-testid="sp-profile-tab" value="profile">
                <Building2 className="h-4 w-4 mr-2" /> Company Profile
              </TabsTrigger>
            </TabsList>

            {/* Dashboard Tab */}
            <TabsContent value="dashboard" className="space-y-6">
              {analytics && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Card className="p-4 text-center" data-testid="sp-total-banners">
                      <p className="text-2xl font-bold text-brand-navy">{analytics.total_banners}</p>
                      <p className="text-xs text-slate-500">Total Banners</p>
                    </Card>
                    <Card className="p-4 text-center" data-testid="sp-active-banners">
                      <p className="text-2xl font-bold text-green-600">{analytics.active_banners}</p>
                      <p className="text-xs text-slate-500">Active Now</p>
                    </Card>
                    <Card className="p-4 text-center" data-testid="sp-total-impressions">
                      <p className="text-2xl font-bold text-brand-blue">{analytics.total_impressions.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">Impressions</p>
                    </Card>
                    <Card className="p-4 text-center" data-testid="sp-total-clicks">
                      <p className="text-2xl font-bold text-amber-500">{analytics.total_clicks.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">Clicks</p>
                    </Card>
                  </div>

                  {analytics.clicks_by_date.length > 0 && (
                    <Card className="p-5" data-testid="sp-clicks-chart">
                      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Click Trends (Last 30 Days)</h3>
                      <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={analytics.clicks_by_date}>
                            <defs>
                              <linearGradient id="clickGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3E60B1" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#3E60B1" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                            <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                            <Area type="monotone" dataKey="clicks" stroke="#3E60B1" fill="url(#clickGrad)" strokeWidth={2} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </Card>
                  )}

                  {analytics.banners.length > 0 && (
                    <Card className="p-5">
                      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">Banner Performance</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left text-xs text-slate-500 uppercase">
                              <th className="pb-2 pr-4">Type</th>
                              <th className="pb-2 pr-4">Placement</th>
                              <th className="pb-2 pr-4">Campaign</th>
                              <th className="pb-2 pr-4">Status</th>
                              <th className="pb-2 pr-4 text-right">Impressions</th>
                              <th className="pb-2 pr-4 text-right">Clicks</th>
                              <th className="pb-2 text-right">CTR</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {analytics.banners.map(b => (
                              <tr key={b.banner_id} className="hover:bg-slate-50">
                                <td className="py-2 pr-4">{b.banner_type === 'top' ? 'Top 728×90' : 'Side 300×250'}</td>
                                <td className="py-2 pr-4 text-slate-500">{b.placement}</td>
                                <td className="py-2 pr-4 text-xs text-slate-500">
                                  {b.campaign_start || b.campaign_end
                                    ? `${b.campaign_start || '—'} → ${b.campaign_end || '—'}`
                                    : 'Always'}
                                </td>
                                <td className="py-2 pr-4">
                                  {b.pending_approval ? (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Pending Approval</span>
                                  ) : (
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                                      b.campaign_status === 'live' ? 'bg-green-100 text-green-700' :
                                      b.campaign_status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                                      b.campaign_status === 'expired' ? 'bg-slate-200 text-slate-600' :
                                      'bg-red-100 text-red-600'
                                    }`}>{b.campaign_status?.charAt(0).toUpperCase() + b.campaign_status?.slice(1)}</span>
                                  )}
                                </td>
                                <td className="py-2 pr-4 text-right">{b.impressions.toLocaleString()}</td>
                                <td className="py-2 pr-4 text-right">{b.clicks.toLocaleString()}</td>
                                <td className="py-2 text-right font-medium">{b.ctr}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  )}
                </>
              )}
            </TabsContent>

            {/* Banners Tab */}
            <TabsContent value="banners" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-800">My Banners</h3>
                <Button data-testid="sp-upload-banner-btn" onClick={() => setShowUpload(true)}
                  className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
                  <Upload className="h-4 w-4 mr-2" /> Upload Banner
                </Button>
              </div>

              {/* Upload Form */}
              {showUpload && (
                <Card className="p-5 space-y-4 border-brand-navy/20" data-testid="sp-banner-upload-form">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-bold">New Banner</h4>
                    <button onClick={() => setShowUpload(false)}><XIcon className="h-4 w-4 text-slate-400" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select value={bannerForm.banner_type} onValueChange={v => setBannerForm(f => ({ ...f, banner_type: v }))}>
                        <SelectTrigger data-testid="sp-banner-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="top">Top Banner (728×90)</SelectItem>
                          <SelectItem value="side">Side Banner (300×250)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Placement</Label>
                      <Select value={bannerForm.placement} onValueChange={v => setBannerForm(f => ({ ...f, placement: v }))}>
                        <SelectTrigger data-testid="sp-banner-placement"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="app">Site-wide</SelectItem>
                          <SelectItem value="forum">Forum</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Click URL</Label>
                    <Input data-testid="sp-banner-url" value={bannerForm.click_url}
                      onChange={e => setBannerForm(f => ({ ...f, click_url: e.target.value }))}
                      placeholder="https://your-website.com" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Campaign Start</Label>
                      <Input data-testid="sp-campaign-start" type="date" value={bannerForm.campaign_start}
                        onChange={e => setBannerForm(f => ({ ...f, campaign_start: e.target.value }))} />
                    </div>
                    <div>
                      <Label className="text-xs">Campaign End</Label>
                      <Input data-testid="sp-campaign-end" type="date" value={bannerForm.campaign_end}
                        onChange={e => setBannerForm(f => ({ ...f, campaign_end: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Banner Image ({bannerForm.banner_type === 'top' ? '728×90px' : '300×250px'})</Label>
                    <Input data-testid="sp-banner-file" type="file" accept="image/*"
                      onChange={e => setBannerForm(f => ({ ...f, file: e.target.files[0] }))} />
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-xs text-amber-700">
                    <Clock className="h-4 w-4 flex-shrink-0" />
                    <span>Uploaded banners require admin approval before they go live.</span>
                  </div>
                  <Button data-testid="sp-submit-banner" onClick={handleUploadBanner} disabled={uploading}
                    className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white">
                    {uploading ? 'Uploading...' : 'Submit Banner for Review'}
                  </Button>
                </Card>
              )}

              {/* Banner list */}
              {banners.length === 0 ? (
                <Card className="p-8 text-center">
                  <Image className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No banners yet. Upload your first banner ad.</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {banners.map(banner => (
                    <Card key={banner.banner_id} data-testid={`sp-banner-${banner.banner_id}`} className="p-4">
                      <div className="flex items-start gap-4">
                        <img src={banner.image_url} alt="Banner" className="h-16 w-24 object-contain rounded border bg-white flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-xs font-medium px-2 py-0.5 bg-brand-navy/10 text-brand-navy rounded">
                              {banner.banner_type === 'top' ? 'Top 728×90' : 'Side 300×250'}
                            </span>
                            <span className="text-xs text-slate-500">{banner.placement}</span>
                            {banner.pending_approval ? (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Pending Approval</span>
                            ) : (
                              <span className={`text-xs px-1.5 py-0.5 rounded ${banner.enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                {banner.enabled ? 'Active' : 'Paused'}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                            <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {banner.impressions} impressions</span>
                            <span className="flex items-center gap-1"><MousePointer className="h-3 w-3" /> {banner.clicks} clicks</span>
                            <span>CTR: {banner.impressions > 0 ? ((banner.clicks / banner.impressions) * 100).toFixed(1) : 0}%</span>
                          </div>
                          {(banner.campaign_start || banner.campaign_end) && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                              <Calendar className="h-3 w-3" />
                              {banner.campaign_start || '—'} to {banner.campaign_end || '—'}
                              {banner.campaign_end && banner.campaign_end < today && <span className="ml-1 text-red-500">(Expired)</span>}
                              {banner.campaign_start && banner.campaign_start > today && <span className="ml-1 text-blue-500">(Not started)</span>}
                            </div>
                          )}
                          {banner.click_url && (
                            <a href={banner.click_url} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 mt-1 text-xs text-brand-blue hover:underline">
                              <ExternalLink className="h-3 w-3" /> {banner.click_url}
                            </a>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-500 flex-shrink-0"
                          onClick={() => handleDeleteBanner(banner.banner_id)} data-testid={`sp-delete-banner-${banner.banner_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-4">
              <Card className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-slate-800">Company Profile</h3>
                  {!editingProfile ? (
                    <Button variant="outline" size="sm" onClick={() => setEditingProfile(true)} data-testid="sp-edit-profile-btn">
                      <Edit className="h-4 w-4 mr-2" /> Edit
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditingProfile(false)}>Cancel</Button>
                      <Button size="sm" onClick={handleSaveProfile} data-testid="sp-save-profile-btn"
                        className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
                        <Save className="h-4 w-4 mr-2" /> Save
                      </Button>
                    </div>
                  )}
                </div>

                {editingProfile ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Company Name</Label>
                      <Input data-testid="sp-name-input" value={profileForm.name}
                        onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <RichTextEditor value={profileForm.description}
                        onChange={v => setProfileForm(f => ({ ...f, description: v }))}
                        testId="sp-desc-input" minHeight="6rem" />
                    </div>
                    <div>
                      <Label>Website</Label>
                      <Input data-testid="sp-website-input" value={profileForm.website}
                        onChange={e => setProfileForm(f => ({ ...f, website: e.target.value }))}
                        placeholder="https://..." />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Company Name</p>
                      <p className="text-sm font-medium text-slate-800">{sponsor.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Description</p>
                      <div className="text-sm text-slate-700">
                        {sponsor.description ? <RichText html={sponsor.description} /> : '—'}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Website</p>
                      {sponsor.website ? (
                        <a href={sponsor.website} target="_blank" rel="noopener noreferrer"
                          className="text-sm text-brand-blue hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3.5 w-3.5" /> {sponsor.website}
                        </a>
                      ) : <p className="text-sm text-slate-400">—</p>}
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Contact Email</p>
                      <p className="text-sm text-slate-700">{sponsor.contact_email || '—'}</p>
                    </div>
                    {sponsor.categories?.length > 0 && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Categories</p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {sponsor.categories.map(c => (
                            <span key={c} className="px-2 py-0.5 bg-brand-sage/15 text-brand-sage text-xs rounded-full">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
